import { listConversations, getConversation, getConversationRaw } from '@harmonyso/conversations-viewer-sdk';
import { getAllTenants, type TenantRead } from '@harmonyso/tenants-sdk';
import axios from 'axios';
import type { ConversationMetadata, ConversationDetail, RawConversation } from '@/lib/types/conversations';
import { downloadCSV, exportToCSV } from './csv-export';
import type { Cell } from '@/lib/cells/cells';
import { compareConversations } from '@/lib/cells/cell-conversations';
import { conversationsViewerCellOptions } from '@/lib/cells/cell-statistics';

export interface ConversationExportFilters {
  search?: string;
  source_identifier?: string;
  category?: string;
  ai_category?: string;
  workflow_type?: string;
  sentiment?: string;
  interest_level?: string;
  resolution?: string;
  source?: string;
  is_starred?: boolean;
  tenant_id?: string;
  min_turns?: number;
  max_turns?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  date_from?: string;
  date_to?: string;
}

/** The cells to export from, what each leaves out, and every cell's tenants for names. */
export interface ConversationExportCells {
  cells: Cell[];
  excludeTenantIds: (cell: Cell) => string[];
  tenants?: TenantRead[];
}

export interface ConversationExportOptions {
  includeConversationContent: boolean;
  includeRawData: boolean;
}

async function fetchAllConversations(
  filters: ConversationExportFilters = {},
  cell?: Cell,
  excludeTenantIds: string[] = [],
): Promise<ConversationMetadata[]> {
  const allConversations: ConversationMetadata[] = [];
  let page = 1;
  let hasMore = true;
  const BATCH_SIZE = 200;

  while (hasMore) {
    const query: Record<string, unknown> = {
      page,
      page_size: BATCH_SIZE,
      ...filters,
      ...(filters.sortBy && { sort_by: filters.sortBy }),
      ...(filters.sortDirection && { sort_direction: filters.sortDirection }),
      ...(excludeTenantIds.length > 0 && { exclude_tenant_ids: excludeTenantIds }),
    };

    const response = await listConversations({
      ...conversationsViewerCellOptions(cell?.id),
      query: query as never,
      throwOnError: true,
    });

    const conversations = response.data.conversations;
    if (conversations.length > 0) {
      allConversations.push(...conversations);
    }

    hasMore = conversations.length === BATCH_SIZE && allConversations.length < response.data.total_count;
    page += 1;
  }

  return allConversations;
}

async function fetchAllTenants(): Promise<Map<string, TenantRead>> {
  const tenantMap = new Map<string, TenantRead>();

  const response = await getAllTenants({ throwOnError: true });
  const tenants = response.data;

  tenants.forEach((tenant: TenantRead) => {
    tenantMap.set(tenant.id, tenant);
  });

  return tenantMap;
}

async function fetchConversationDetail(conversationId: string, tenantId: string): Promise<ConversationDetail | null> {
  try {
    const response = await getConversation({
      path: { conversation_id: conversationId },
      query: { tenant_id: tenantId },
      throwOnError: true,
    });
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch conversation ${conversationId}:`, error);
    return null;
  }
}

async function fetchRawConversationData(conversationId: string, tenantId: string): Promise<RawConversation | null> {
  try {
    // First get the presigned S3 URL
    const urlResponse = await getConversationRaw({
      path: { conversation_id: conversationId },
      query: { tenant_id: tenantId },
      throwOnError: true,
    });

    if (!urlResponse.data.url) {
      console.error(`No S3 URL available for conversation ${conversationId}`);
      return null;
    }

    // Then fetch the raw JSON from S3
    const s3Response = await axios.get<RawConversation>(urlResponse.data.url);
    return s3Response.data;
  } catch (error) {
    console.error(`Failed to fetch raw S3 data for conversation ${conversationId}:`, error);
    return null;
  }
}

interface TicketCreatedData {
  title?: string;
  priority?: string;
  ticket_type?: string;
}

function formatConversationTurns(conversation: ConversationDetail | null): string {
  if (!conversation?.turns || conversation.turns.length === 0) {
    return '[No conversation data]';
  }

  const formattedTurns = conversation.turns.map((turn) => {
    const lines: string[] = [];

    // Build the turn header with role
    const role = turn.role === 'user' ? 'USER' : 'AGENT';
    const turnNum = (turn.turn_index || 0) + 1;
    lines.push(`[TURN ${turnNum} - ${role}]`);

    // Add employee name for user messages
    if (turn.employee_name) {
      lines.push(`From: ${turn.employee_name}`);
    }

    // Add timestamp
    const ts = typeof turn.timestamp === 'string' ? turn.timestamp : turn.timestamp.toISOString();
    lines.push(`Time: ${ts}`);

    // Add the actual message content
    if (turn.message_text) {
      lines.push(''); // blank line before message
      lines.push(turn.message_text);
    }

    // Add ticket info if created
    if (turn.extra_data && typeof turn.extra_data === 'object' && 'ticket_created' in turn.extra_data) {
      const ticketData = turn.extra_data.ticket_created as TicketCreatedData;
      lines.push(''); // blank line
      lines.push(`[Ticket Created: ${ticketData.title || 'N/A'}]`);
      if (ticketData.priority) lines.push(`Priority: ${ticketData.priority}`);
      if (ticketData.ticket_type) lines.push(`Type: ${ticketData.ticket_type}`);
    }

    return lines.join('\n');
  });

  return formattedTurns.join('\n\n---\n\n');
}

function conversationToCSVRow(
  conversation: ConversationMetadata,
  tenantMap: Map<string, TenantRead>,
  conversationContent: string | null,
  rawS3Data: RawConversation | null,
  options: ConversationExportOptions
): Record<string, unknown> {
  const tenant = tenantMap.get(conversation.tenant_id);
  const tenantName = tenant?.company || '';

  const row: Record<string, unknown> = {
    'Conversation ID': conversation.id,
    'Title': conversation.title || '',
    'Tenant ID': conversation.tenant_id,
    'Tenant Name': tenantName,
    'Category': conversation.category || '',
    'Subcategory': conversation.subcategory || '',
    'AI Category': conversation.ai_category || '',
    'AI Subcategory': conversation.ai_subcategory || '',
    'Workflow Types': conversation.workflow_types?.join(', ') || '',
    'Turn Count': conversation.turn_count,
    'Started At': conversation.conversation_date,
    'Last Turn At': conversation.last_turn_at || '',
    'Initiated By': conversation.initiated_by_employee_name || '',
    'Source Identifier': conversation.source_identifier || '',
    'Is Starred': conversation.is_starred ? 'Yes' : 'No',
  };

  // Conditionally add conversation content column
  if (options.includeConversationContent) {
    row.Conversation = conversationContent || '[No conversation data]';
  }

  // Conditionally add raw data column
  if (options.includeRawData) {
    const rawData = rawS3Data ? JSON.stringify(rawS3Data, null, 2) : '[No raw S3 data available]';
    row['Raw Data'] = rawData;
  }

  return row;
}

export async function exportConversationsWithFilters(
  filters: ConversationExportFilters = {},
  options: ConversationExportOptions = { includeConversationContent: true, includeRawData: false },
  onProgress?: (current: number, total: number) => void,
  cells?: ConversationExportCells,
): Promise<void> {
  const fetchConversations = async (): Promise<ConversationMetadata[]> => {
    if (!cells || cells.cells.length === 0) {
      return fetchAllConversations(filters);
    }
    // A cell that fails fails the export, so a file is never silently partial.
    const perCell = await Promise.all(
      cells.cells.map((cell) => fetchAllConversations(filters, cell, cells.excludeTenantIds(cell))),
    );
    const merged = perCell.flat();
    if (cells.cells.length > 1 && filters.sortBy) {
      merged.sort(compareConversations(filters.sortBy, filters.sortDirection ?? 'desc') as never);
    }
    return merged;
  };
  const fetchTenantMap = async (): Promise<Map<string, TenantRead>> =>
    cells?.tenants ? new Map(cells.tenants.map((tenant) => [tenant.id, tenant])) : fetchAllTenants();

  const [conversations, tenantMap] = await Promise.all([fetchConversations(), fetchTenantMap()]);

  const totalConversations = conversations.length;
  const rows: Record<string, unknown>[] = [];

  // If neither option is selected, just export metadata without fetching details
  if (!options.includeConversationContent && !options.includeRawData) {
    for (const conversation of conversations) {
      rows.push(conversationToCSVRow(conversation, tenantMap, null, null, options));
    }
    if (onProgress) {
      onProgress(totalConversations, totalConversations);
    }
  } else {
    // Fetch conversation details in batches to avoid overwhelming the API
    const BATCH_SIZE = 10;
    for (let i = 0; i < conversations.length; i += BATCH_SIZE) {
      const batch = conversations.slice(i, i + BATCH_SIZE);

      // Only fetch the data we actually need based on options
      const fetchPromises = batch.map(async (conv) => {
        const [detail, rawData] = await Promise.all([
          options.includeConversationContent
            ? fetchConversationDetail(conv.id, conv.tenant_id)
            : Promise.resolve(null),
          options.includeRawData
            ? fetchRawConversationData(conv.id, conv.tenant_id)
            : Promise.resolve(null),
        ] as const);
        return { conversation: conv, detail, rawData };
      });
      const results = await Promise.all(fetchPromises);

      // Create CSV rows for this batch
      for (const { conversation, detail, rawData } of results) {
        const conversationContent = options.includeConversationContent ? formatConversationTurns(detail) : null;
        rows.push(conversationToCSVRow(conversation, tenantMap, conversationContent, rawData, options));
      }

      // Update progress
      if (onProgress) {
        onProgress(Math.min(i + BATCH_SIZE, totalConversations), totalConversations);
      }
    }
  }

  const csvContent = exportToCSV(rows, (row) => row);
  const timestamp = new Date().toISOString().split('T')[0];
  const filename = `harmony-conversations-export-${timestamp}.csv`;

  downloadCSV(csvContent, filename);
}
